import React, { useEffect, useState } from 'react';
import { getGalleryBySlug } from '../lib/cms';
import useSEO from '../hooks/useSEO';
import GalleryLightbox from '../components/GalleryLightbox';

export default function GalleriPage() {
  useSEO(
    'Billedgalleri | Chokolenten ApS',
    'Se vores billedgalleri med håndlavede chokolader, Thy Kukies, kager, gaveæsker og stemningsbilleder fra vores hyggelige butik i Hanstholm.'
  );

  const [activeTab, setActiveTab] = useState('alle');
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const saesonKukiesImages = [
    'https://assets.auxodev.dk/assets/2c62fa2f-a0e0-45c6-84dc-1b9d3147e5ca/thy-zombie-kukie-1.jpg',
    'https://assets.auxodev.dk/assets/2c62fa2f-a0e0-45c6-84dc-1b9d3147e5ca/sommer-citron.jpg'
  ];

  const tabs = [
    { slug: 'alle', name: 'Vis alle' },
    { slug: 'produkter', name: 'Vores produkter' },
    { slug: 'butiksmiljoe', name: 'butikken' },
    { slug: 'saesonvarer', name: 'Sæson varer' }
  ];

  useEffect(() => {
    setLoading(true);
    if (activeTab === 'alle') {
      Promise.all([
        getGalleryBySlug('produkter'),
        getGalleryBySlug('froeer'),
        getGalleryBySlug('butiksmiljoe'),
        getGalleryBySlug('saesonvarer')
      ]).then(([prod, froeer, butik, saeson]) => {
        const allImages = [
          ...(prod?.images || []),
          ...(froeer?.images || []),
          ...(butik?.images || []),
          ...(saeson?.images || []),
          ...saesonKukiesImages
        ];
        // Remove duplicates if any
        const uniqueImages = [...new Set(allImages)];
        setGallery({
          title: { da: 'Alle billeder' },
          description: { da: 'Se hele vores galleri af håndlavede chokolader, Thy Kukies, kager, gaveæsker og hyggelig butiksstemning.' },
          images: uniqueImages
        });
        setLoading(false);
      }).catch(() => {
        setLoading(false);
      });
    } else if (activeTab === 'produkter') {
      Promise.all([
        getGalleryBySlug('produkter'),
        getGalleryBySlug('froeer')
      ]).then(([prod, froeer]) => {
        const combinedImages = [
          ...(prod?.images || []),
          ...(froeer?.images || [])
        ];
        const uniqueImages = [...new Set(combinedImages)];
        setGallery({
          title: prod?.title || { da: 'Vores produkter' },
          description: prod?.description || { da: 'Et udvalg af vores håndlavede chokolader, Kukies, kager og gaveæsker.' },
          images: uniqueImages
        });
        setLoading(false);
      }).catch(() => {
        setLoading(false);
      });
    } else if (activeTab === 'saesonvarer') {
      getGalleryBySlug('saesonvarer').then(data => {
        const combinedImages = [
          ...(data?.images || []),
          ...saesonKukiesImages
        ];
        const uniqueImages = [...new Set(combinedImages)];
        setGallery({
          title: data?.title || { da: 'Sæsonens specialiteter' },
          description: data?.description || { da: 'Vores udvalg af sæsonens lækkerier og højtids-Kukies.' },
          images: uniqueImages
        });
        setLoading(false);
      }).catch(() => {
        setLoading(false);
      });
    } else {
      getGalleryBySlug(activeTab).then(data => {
        setGallery(data);
        setLoading(false);
      }).catch(() => {
        setLoading(false);
      });
    }
  }, [activeTab]);

  const images = gallery?.images || [];
  const title = gallery?.title?.da || gallery?.title || '';
  const description = gallery?.description?.da || gallery?.description || '';

  return (
    <div className="pb-20">
      
      {/* Hero Section */}
      <section className="relative py-24 bg-choc-dark text-cream-light overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/2c62fa2f-a0e0-45c6-84dc-1b9d3147e5ca/imgi-117-699939537-1585083166953793-4642000396741448814-n.jpg"
            alt="Galleri baggrund"
            className="w-full h-full object-cover opacity-50 scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-choc-dark/60 via-choc-dark/30 to-transparent" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center gap-4">
          <span className="text-xs font-sans uppercase tracking-[0.25em] text-berry-light font-bold">
            Visuel nydelse
          </span>
          <h1 className="text-4xl sm:text-5xl font-serif font-bold text-cream-light tracking-wide">
            Vores Galleri
          </h1>
          <p className="max-w-2xl text-sm sm:text-base text-cream-light/80 leading-relaxed font-sans">
            Gå på opdagelse i vores visuelle univers og lad dig friste af vores håndlavede chokolader, Thy Kukies, kager og den hyggelige stemning i butikken.
          </p>
        </div>
      </section>

      {/* Galleri Vælger (Tabs) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20">
        <div className="bg-cream-light rounded-2xl border border-choc-dark/10 shadow-md p-2 flex flex-wrap items-center justify-center gap-1 sm:gap-2">
          {tabs.map((tab) => {
            const isSelected = activeTab === tab.slug;
            return (
              <button
                key={tab.slug}
                onClick={() => setActiveTab(tab.slug)}
                className={`px-5 py-3 rounded-xl font-sans text-sm font-semibold transition-all cursor-pointer ${
                  isSelected
                    ? 'bg-berry-accent text-white shadow-sm'
                    : 'text-choc-dark/70 hover:text-choc-dark hover:bg-choc-dark/5'
                }`}
              >
                {tab.name}
              </button>
            );
          })}
        </div>
      </section>

      {/* Galleri Beskrivelse & Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-12 h-12 rounded-full border-4 border-berry-accent/20 border-t-berry-accent animate-spin" />
            <p className="text-sm text-choc-dark/50 font-sans">Henter billeder...</p>
          </div>
        ) : (
          <div className="flex flex-col gap-10">
            {/* Galleri Info */}
            {title && (
              <div className="text-center max-w-2xl mx-auto flex flex-col gap-2">
                <h2 className="text-2xl sm:text-3xl font-serif font-bold text-choc-dark">
                  {title}
                </h2>
                {description && (
                  <p className="text-sm sm:text-base text-choc-dark/70 font-sans">
                    {description}
                  </p>
                )}
              </div>
            )}

            {/* Billeder Grid */}
            {images.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {images.map((img, index) => (
                  <div
                    key={index}
                    onClick={() => setLightboxIndex(index)}
                    className="aspect-square rounded-2xl overflow-hidden border border-choc-dark/10 shadow-sm cursor-pointer group relative bg-choc-dark/5"
                  >
                    <img
                      src={img.replace('https://assets.auxodev.dk/assets/', 'https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=85,format=auto/assets/')}
                      alt={`Galleri billede ${index + 1}`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-choc-dark/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <i className="fa-regular fa-eye text-white text-2xl" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 bg-cream-light rounded-2xl border border-choc-dark/10 p-8 max-w-xl mx-auto flex flex-col gap-4">
                <div className="w-16 h-16 rounded-full bg-berry-accent/10 flex items-center justify-center text-berry-accent mx-auto">
                  <i className="fa-regular fa-images text-2xl" />
                </div>
                <h3 className="font-serif font-bold text-xl text-choc-dark">
                  Galleriet er tomt lige nu
                </h3>
                <p className="text-sm text-choc-dark/70 font-sans">
                  Vi bager og laver hele tiden nye lækkerier. Vi tilføjer snart nye billeder til dette galleri!
                </p>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Lightbox Integration */}
      {lightboxIndex !== null && images.length > 0 && (
        <GalleryLightbox
          images={images}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex((lightboxIndex - 1 + images.length) % images.length)}
          onNext={() => setLightboxIndex((lightboxIndex + 1) % images.length)}
        />
      )}

    </div>
  );
}
